/**
 * 人物状态管理
 * 管理所有角色数据
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  Character,
  CharacterFilter,
  CharacterIdentity,
} from '../types/character'
import type { GameDate } from '../types/game'

// ============================================
// 状态定义
// ============================================

interface CharacterState {
  // 所有角色
  characters: Map<string, Character>

  // 势力人物索引 (factionId -> characterIds[])
  factionCharacterIndex: Map<string, string[]>

  // 当前选中角色
  selectedCharacterId: string | null

  // ID计数器
  idCounter: number
}

interface CharacterActions {
  // 初始化
  initializeCharacters: (characters: Character[]) => void
  resetCharacters: () => void

  // 角色操作
  addCharacter: (character: Character) => void
  updateCharacter: (characterId: string, updates: Partial<Character>) => void
  removeCharacter: (characterId: string) => void

  // 批量操作
  addCharacters: (characters: Character[]) => void
  removeCharactersByFaction: (factionId: string) => void

  // 查询
  getCharacter: (characterId: string) => Character | undefined
  getCharactersByFaction: (factionId: string) => Character[]
  getCharactersByIdentity: (identity: CharacterIdentity) => Character[]
  getRuler: (factionId: string) => Character | undefined
  getHeir: (factionId: string) => Character | undefined
  getOfficials: (factionId: string) => Character[]
  getRoyalMembers: (factionId: string) => Character[]
  getCommoners: (factionId: string) => Character[]
  filterCharacters: (filter: CharacterFilter) => Character[]

  // 关系操作
  addRelation: (characterId: string, relationType: keyof Character['relations'], targetId: string) => void
  removeRelation: (characterId: string, relationType: keyof Character['relations'], targetId: string) => void

  // 状态变更
  killCharacter: (characterId: string, date: GameDate, reason: string) => void
  promoteCharacter: (characterId: string, newTitle: string, newRank: number) => void

  // 选择
  selectCharacter: (characterId: string | null) => void

  // 统计
  getFactionCharacterCount: (factionId: string) => number
  getFactionStats: (factionId: string) => {
    total: number
    royal: number
    official: number
    commoner: number
    averageLoyalty: number
  }

  // 序列化/反序列化
  serialize: () => string
  deserialize: (data: string) => void
}

// ============================================
// Store 创建
// ============================================

export const useCharacterStore = create<CharacterState & CharacterActions>()(
  persist(
    (set, get) => ({
      // 初始状态
      characters: new Map(),
      factionCharacterIndex: new Map(),
      selectedCharacterId: null,
      idCounter: 0,

      // ========================================
      // 初始化
      // ========================================

      initializeCharacters: (characters) => {
        const charMap = new Map<string, Character>()
        const factionIndex = new Map<string, string[]>()

        characters.forEach(char => {
          charMap.set(char.id, char)

          // 建立势力索引
          if (!factionIndex.has(char.factionId)) {
            factionIndex.set(char.factionId, [])
          }
          factionIndex.get(char.factionId)!.push(char.id)
        })

        set({
          characters: charMap,
          factionCharacterIndex: factionIndex,
          idCounter: characters.length,
        })
      },

      resetCharacters: () => {
        set({
          characters: new Map(),
          factionCharacterIndex: new Map(),
          selectedCharacterId: null,
          idCounter: 0,
        })
      },

      // ========================================
      // 角色操作
      // ========================================

      addCharacter: (character) => {
        const state = get()
        const newCharacters = new Map(state.characters)
        newCharacters.set(character.id, character)

        // 更新势力索引
        const newIndex = new Map(state.factionCharacterIndex)
        if (!newIndex.has(character.factionId)) {
          newIndex.set(character.factionId, [])
        }
        newIndex.get(character.factionId)!.push(character.id)

        set({
          characters: newCharacters,
          factionCharacterIndex: newIndex,
          idCounter: state.idCounter + 1,
        })
      },

      updateCharacter: (characterId, updates) => {
        const state = get()
        const character = state.characters.get(characterId)
        if (!character) return

        const newCharacters = new Map(state.characters)
        newCharacters.set(characterId, { ...character, ...updates })

        set({ characters: newCharacters })
      },

      removeCharacter: (characterId) => {
        const state = get()
        const character = state.characters.get(characterId)
        if (!character) return

        const newCharacters = new Map(state.characters)
        newCharacters.delete(characterId)

        // 更新势力索引
        const newIndex = new Map(state.factionCharacterIndex)
        const factionChars = newIndex.get(character.factionId)
        if (factionChars) {
          const updated = factionChars.filter(id => id !== characterId)
          if (updated.length === 0) {
            newIndex.delete(character.factionId)
          } else {
            newIndex.set(character.factionId, updated)
          }
        }

        set({
          characters: newCharacters,
          factionCharacterIndex: newIndex,
        })
      },

      addCharacters: (characters) => {
        const state = get()
        const newCharacters = new Map(state.characters)
        const newIndex = new Map(state.factionCharacterIndex)

        characters.forEach(char => {
          newCharacters.set(char.id, char)

          if (!newIndex.has(char.factionId)) {
            newIndex.set(char.factionId, [])
          }
          newIndex.get(char.factionId)!.push(char.id)
        })

        set({
          characters: newCharacters,
          factionCharacterIndex: newIndex,
          idCounter: state.idCounter + characters.length,
        })
      },

      removeCharactersByFaction: (factionId) => {
        const state = get()
        const factionChars = state.factionCharacterIndex.get(factionId)
        if (!factionChars) return

        const newCharacters = new Map(state.characters)
        factionChars.forEach(id => {
          newCharacters.delete(id)
        })

        const newIndex = new Map(state.factionCharacterIndex)
        newIndex.delete(factionId)

        set({
          characters: newCharacters,
          factionCharacterIndex: newIndex,
        })
      },

      // ========================================
      // 查询
      // ========================================

      getCharacter: (characterId) => {
        return get().characters.get(characterId)
      },

      getCharactersByFaction: (factionId) => {
        const state = get()
        const charIds = state.factionCharacterIndex.get(factionId) || []
        return charIds.map(id => state.characters.get(id)!).filter(Boolean)
      },

      getCharactersByIdentity: (identity) => {
        const state = get()
        return Array.from(state.characters.values()).filter(
          char => char.identity === identity
        )
      },

      getRuler: (factionId) => {
        const state = get()
        const charIds = state.factionCharacterIndex.get(factionId) || []
        return charIds
          .map(id => state.characters.get(id)!)
          .find(char => char.position?.title === '王' || char.position?.title === '侯')
      },

      getHeir: (factionId) => {
        const state = get()
        const charIds = state.factionCharacterIndex.get(factionId) || []
        return charIds
          .map(id => state.characters.get(id)!)
          .find(char => {
            if (char.identity !== 'royal') return false
            const royalChar = char as Character & { royalInfo?: { successionOrder?: number } }
            return royalChar.royalInfo?.successionOrder === 1
          })
      },

      getOfficials: (factionId) => {
        const state = get()
        const charIds = state.factionCharacterIndex.get(factionId) || []
        return charIds
          .map(id => state.characters.get(id)!)
          .filter(char => char.identity === 'official')
      },

      getRoyalMembers: (factionId) => {
        const state = get()
        const charIds = state.factionCharacterIndex.get(factionId) || []
        return charIds
          .map(id => state.characters.get(id)!)
          .filter(char => char.identity === 'royal')
      },

      getCommoners: (factionId) => {
        const state = get()
        const charIds = state.factionCharacterIndex.get(factionId) || []
        return charIds
          .map(id => state.characters.get(id)!)
          .filter(char => char.identity === 'commoner')
      },

      filterCharacters: (filter) => {
        const state = get()
        let result = Array.from(state.characters.values())

        if (filter.factionId) {
          result = result.filter(char => char.factionId === filter.factionId)
        }

        if (filter.identity) {
          result = result.filter(char => char.identity === filter.identity)
        }

        if (filter.status) {
          result = result.filter(char => char.status === filter.status)
        }

        if (filter.minLoyalty !== undefined) {
          result = result.filter(char => char.loyalty >= filter.minLoyalty!)
        }

        if (filter.maxLoyalty !== undefined) {
          result = result.filter(char => char.loyalty <= filter.maxLoyalty!)
        }

        if (filter.minAbility !== undefined) {
          result = result.filter(char => {
            const avgAbility =
              (char.abilities.intelligence +
                char.abilities.charisma +
                char.abilities.military +
                char.abilities.administration +
                char.abilities.diplomacy +
                char.abilities.intrigue) /
              6
            return avgAbility >= filter.minAbility!
          })
        }

        if (filter.hasPosition !== undefined) {
          result = result.filter(char =>
            filter.hasPosition ? !!char.position : !char.position
          )
        }

        return result
      },

      // ========================================
      // 关系操作
      // ========================================

      addRelation: (characterId, relationType, targetId) => {
        const state = get()
        const character = state.characters.get(characterId)
        if (!character) return

        const newCharacters = new Map(state.characters)
        const relations = { ...character.relations }

        if (relationType === 'childrenIds' || relationType === 'siblingIds' ||
            relationType === 'friendIds' || relationType === 'enemyIds') {
          if (!relations[relationType].includes(targetId)) {
            relations[relationType] = [...relations[relationType], targetId]
          }
        } else {
          relations[relationType] = targetId
        }

        newCharacters.set(characterId, { ...character, relations })
        set({ characters: newCharacters })
      },

      removeRelation: (characterId, relationType, targetId) => {
        const state = get()
        const character = state.characters.get(characterId)
        if (!character) return

        const newCharacters = new Map(state.characters)
        const relations = { ...character.relations }

        if (relationType === 'childrenIds' || relationType === 'siblingIds' ||
            relationType === 'friendIds' || relationType === 'enemyIds') {
          relations[relationType] = relations[relationType].filter(id => id !== targetId)
        } else {
          delete relations[relationType]
        }

        newCharacters.set(characterId, { ...character, relations })
        set({ characters: newCharacters })
      },

      // ========================================
      // 状态变更
      // ========================================

      killCharacter: (characterId, date, reason) => {
        const state = get()
        const character = state.characters.get(characterId)
        if (!character) return

        const newCharacters = new Map(state.characters)
        newCharacters.set(characterId, {
          ...character,
          status: 'deceased',
          deathDate: date,
          history: [
            ...character.history,
            {
              id: `event_${Date.now()}`,
              type: 'death',
              date,
              title: '去世',
              description: reason,
            },
          ],
        })

        set({ characters: newCharacters })
      },

      promoteCharacter: (characterId, newTitle, newRank) => {
        const state = get()
        const character = state.characters.get(characterId)
        if (!character) return

        const newCharacters = new Map(state.characters)
        newCharacters.set(characterId, {
          ...character,
          position: {
            ...character.position,
            title: newTitle,
            rank: newRank,
          },
        })

        set({ characters: newCharacters })
      },

      // ========================================
      // 选择
      // ========================================

      selectCharacter: (characterId) => {
        set({ selectedCharacterId: characterId })
      },

      // ========================================
      // 统计
      // ========================================

      getFactionCharacterCount: (factionId) => {
        const state = get()
        return state.factionCharacterIndex.get(factionId)?.length || 0
      },

      getFactionStats: (factionId) => {
        const state = get()
        const charIds = state.factionCharacterIndex.get(factionId) || []
        const characters = charIds.map(id => state.characters.get(id)!).filter(Boolean)

        const stats = {
          total: characters.length,
          royal: 0,
          official: 0,
          commoner: 0,
          averageLoyalty: 0,
        }

        let totalLoyalty = 0

        characters.forEach(char => {
          switch (char.identity) {
            case 'royal':
              stats.royal++
              break
            case 'official':
              stats.official++
              break
            case 'commoner':
              stats.commoner++
              break
          }
          totalLoyalty += char.loyalty
        })

        if (characters.length > 0) {
          stats.averageLoyalty = Math.round(totalLoyalty / characters.length)
        }

        return stats
      },

      // ========================================
      // 序列化/反序列化
      // ========================================

      serialize: () => {
        const state = get()
        return JSON.stringify({
          characters: Array.from(state.characters.entries()),
          factionCharacterIndex: Array.from(state.factionCharacterIndex.entries()),
          idCounter: state.idCounter,
        })
      },

      deserialize: (data) => {
        const parsed = JSON.parse(data)
        set({
          characters: new Map(parsed.characters),
          factionCharacterIndex: new Map(parsed.factionCharacterIndex),
          idCounter: parsed.idCounter,
        })
      },
    }),
    {
      name: 'character-storage',
      partialize: (state) => ({
        characters: Array.from(state.characters.entries()),
        factionCharacterIndex: Array.from(state.factionCharacterIndex.entries()),
        idCounter: state.idCounter,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.characters = new Map(state.characters as unknown as [string, Character][])
          state.factionCharacterIndex = new Map(state.factionCharacterIndex as unknown as [string, string[]][])
        }
      },
    }
  )
)
